// src/components/DataTable.jsx
// Brutalist data table component
import { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const DataTable = ({
    columns,
    data,
    loading = false,
    pagination = true,
    pageSize = 10,
    onRowClick = null,
    className = ''
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Sort data
    let sortedData = [...data];
    if (sortConfig.key) {
        sortedData.sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }

    // Paginate data
    const totalPages = Math.ceil(sortedData.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const paginatedData = sortedData.slice(startIndex, startIndex + pageSize);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const handlePageChange = (page) => {
        setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    };

    if (loading) {
        return (
            <div className="surface-card">
                <div className="px-4 py-8 text-center">
                    <div className="inline-block w-5 h-5 border-2 border-gray-600 border-t-primary rounded-full animate-spin" />
                    <p className="mt-2 font-mono text-xs text-gray-500 uppercase">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`surface-card overflow-hidden ${className}`}>
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="border-b border-line bg-slate-50">
                        <tr>
                            {columns.map((column) => (
                                <th
                                    key={column.key}
                                    className={`px-4 py-3 text-left font-mono text-[11px] font-semibold text-gray-500 uppercase tracking-wider transition-colors ${
                                        column.sortable ? 'cursor-pointer hover:text-gray-200' : ''
                                    }`}
                                    onClick={() => column.sortable && handleSort(column.key)}
                                >
                                    <div className="flex items-center gap-1">
                                        <span>{column.title}</span>
                                        {column.sortable && sortConfig.key === column.key && (
                                            <span className="text-primary">
                                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-line">
                        {paginatedData.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="px-4 py-10 text-center font-mono text-xs text-gray-600 uppercase">
                                    No data available
                                </td>
                            </tr>
                        ) : (
                            paginatedData.map((row, index) => (
                                <tr
                                    key={row.id || index}
                                    className={`transition-colors ${onRowClick ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                                    onClick={() => onRowClick && onRowClick(row)}
                                >
                                    {columns.map((column) => (
                                        <td key={column.key} className="px-4 py-3 text-sm text-gray-300">
                                            {column.render ? column.render(row[column.key], row) : row[column.key]}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {pagination && totalPages > 1 && (
                <div className="px-4 py-3 flex items-center justify-between border-t border-line bg-slate-50">
                    <div className="font-mono text-xs text-gray-500">
                        {startIndex + 1}-{Math.min(startIndex + pageSize, sortedData.length)} of {sortedData.length}
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-2 py-1 rounded-md border border-line text-gray-400 hover:bg-slate-100 hover:text-white hover:border-line-strong disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
                        >
                            <ChevronLeftIcon className="h-4 w-4" />
                        </button>

                        {/* Page numbers */}
                        {(() => {
                            const pages = [];
                            const maxVisiblePages = 5;

                            if (totalPages <= maxVisiblePages) {
                                for (let i = 1; i <= totalPages; i++) {
                                    pages.push(i);
                                }
                            } else {
                                pages.push(1);

                                let startPage = Math.max(2, currentPage - 1);
                                let endPage = Math.min(totalPages - 1, currentPage + 1);

                                if (currentPage <= 3) {
                                    endPage = Math.min(totalPages - 1, 4);
                                } else if (currentPage >= totalPages - 2) {
                                    startPage = Math.max(2, totalPages - 3);
                                }

                                if (startPage > 2) {
                                    pages.push('...');
                                }

                                for (let i = startPage; i <= endPage; i++) {
                                    pages.push(i);
                                }

                                if (endPage < totalPages - 1) {
                                    pages.push('...');
                                }

                                pages.push(totalPages);
                            }

                            return pages.map((page, idx) => (
                                typeof page === 'string' ? (
                                    <span key={`ellipsis-${idx}`} className="px-2 py-1 font-mono text-xs text-gray-600">
                                        {page}
                                    </span>
                                ) : (
                                    <button
                                        key={page}
                                        onClick={() => handlePageChange(page)}
                                        className={`px-3 py-1 rounded-md font-mono text-xs border transition-all duration-150 ${
                                            currentPage === page
                                                ? 'bg-primary-gradient text-black border-transparent font-bold shadow-glow'
                                                : 'border-line text-gray-400 hover:bg-slate-100 hover:text-white hover:border-line-strong'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                )
                            ));
                        })()}

                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-2 py-1 rounded-md border border-line text-gray-400 hover:bg-slate-100 hover:text-white hover:border-line-strong disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
                        >
                            <ChevronRightIcon className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataTable;
